/**
 * Brand Color Theme Utility
 *
 * Provides functions to dynamically apply brand colors to the booking page
 * Uses Chroma.js for perceptually accurate color manipulation
 * Ensures WCAG accessibility compliance
 */

import chroma from 'chroma-js';

/**
 * Determine if text should be light or dark based on background color
 * Uses WCAG 2.1 contrast ratio guidelines
 */
export function getTextColorForBackground(bgColor: string): string {
  try {
    // Use chroma.js for more accurate contrast calculation
    const contrastWithWhite = chroma.contrast(bgColor, '#FFFFFF');
    const contrastWithBlack = chroma.contrast(bgColor, '#000000');

    // Return white text if it has better contrast (WCAG AA requires 4.5:1 for normal text)
    return contrastWithWhite > contrastWithBlack ? '#FFFFFF' : '#000000';
  } catch (error) {
    // Fallback to simple luminance check
    const color = chroma(bgColor);
    return color.luminance() > 0.5 ? '#000000' : '#FFFFFF';
  }
}

/**
 * Apply brand color theme to booking page
 * Sets CSS custom properties for dynamic theming
 *
 * Generates perceptually accurate color variations:
 * - Hover state: Slightly darker for interactivity feedback
 * - Active state: Even darker for pressed state
 * - Light backgrounds: Lightened + desaturated for subtle backgrounds
 * - Very light backgrounds: Maximum lightness for minimal color presence
 * - Text color: Automatically calculated for WCAG compliance
 */
export function applyBrandColor(color: string): void {
  // Validate hex color
  if (!/^#[0-9A-F]{6}$/i.test(color)) {
    return;
  }

  try {
    const root = document.documentElement;
    const baseColor = chroma(color);

    // Set primary brand color
    root.style.setProperty('--brand-color', color);

    // Generate hover state (perceptually darker)
    // Using darken(0.5) creates a natural, visible darkening effect
    const hoverColor = baseColor.darken(0.5).hex();
    root.style.setProperty('--brand-color-hover', hoverColor);

    // Generate active/pressed state (even darker)
    // Using darken(1) creates a clear pressed state
    const activeColor = baseColor.darken(1).hex();
    root.style.setProperty('--brand-color-active', activeColor);

    // Generate light background tint (for hover states)
    // Use HSL to maintain visible color tint
    // Target: 80% lightness, 70% of original saturation
    const hsl = baseColor.hsl();
    const lightColor = chroma.hsl(hsl[0], hsl[1] * 0.7, 0.80).hex();
    root.style.setProperty('--brand-color-light', lightColor);

    // Generate very light background tint (for backgrounds and borders)
    // Use HSL to create a subtle but visible color tint
    // Target: 94% lightness, 65% of original saturation
    // This matches the original design system colors (e.g., #E8F4FA)
    const veryLightColor = chroma.hsl(hsl[0], hsl[1] * 0.65, 0.94).hex();
    root.style.setProperty('--brand-color-very-light', veryLightColor);

    // Determine and set text color for accessibility
    const textColor = getTextColorForBackground(color);
    root.style.setProperty('--brand-text-color', textColor);

    // Set focus ring color (slightly transparent version of brand color)
    // Using alpha transparency for better blend with backgrounds
    const focusColor = baseColor.alpha(0.5).css();
    root.style.setProperty('--brand-color-focus', focusColor);

  } catch (error) {
    // Set default color on error
    applyBrandColor('#2E7AA0');
  }
}

/**
 * Remove brand color theme (revert to defaults)
 */
export function removeBrandColor(): void {
  const root = document.documentElement;

  root.style.removeProperty('--brand-color');
  root.style.removeProperty('--brand-color-hover');
  root.style.removeProperty('--brand-color-active');
  root.style.removeProperty('--brand-color-light');
  root.style.removeProperty('--brand-color-very-light');
  root.style.removeProperty('--brand-text-color');
  root.style.removeProperty('--brand-color-focus');
}

/**
 * Get the currently applied brand color
 */
export function getCurrentBrandColor(): string | null {
  const root = document.documentElement;
  const color = root.style.getPropertyValue('--brand-color');
  return color || null;
}

const brandColorTheme = {
  applyBrandColor,
  removeBrandColor,
  getCurrentBrandColor,
  getTextColorForBackground,
};

export default brandColorTheme;
